import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useCountryContext } from '../../../contexts/CountryProvider';
import useCountryActions from '../../../hooks/APIHooks/users/useCountryActions';
import { Country } from '../../../types/countries';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';
import EditCountryModal from '../../CommonComps/Modals/EditCountryModal';

const CountryModalContainer = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    targetedCountry,
    showDeleteModal,
    showEditModal,
    setTargetedCountry,
    setShowDeleteModal,
    setShowEditModal,
  } = useCountryContext();

  // integration of custom API hooks here
  const { deleteCountryByIdHandler } = useCountryActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle delete country feature declared here
  const deleteCountryHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteCountryByIdHandler
        .mutateAsync({ countryId: targetedCountry?.id as number })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Country Deleted!!');

            setShowDeleteModal(false);
            setTargetedCountry(null);
            queryClient.invalidateQueries({ queryKey: ['country-list'] });
          } else {
            toast.error(res.data.message || 'Country Deletion Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering country modal container here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showEditModal ? (
          <EditCountryModal
            country={targetedCountry as Country}
            handleModalClose={() => setShowEditModal(false)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal ? (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this country{' '}
                <span className='font-semibold text-base-light-blue'>
                  ({targetedCountry?.country_name})
                </span>
                ? Once you delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteCountryHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
};

export default CountryModalContainer;
